/**
 * @fileoverview Module for `src/contexts/AuthContext.js`.
 */

import auth0 from 'auth0-js';
import axios from 'axios';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { createContext, useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from 'react-query';

import deprecatedConfig from '../lib/deprecatedConfig';
import isInvalidLoginError from '../lib/functions/isInvalidLoginError';
import isNonEmptyString from '../lib/functions/isNonEmptyString';
import logger from '../lib/logger';
import Tracker from '../lib/Tracker';
import { extraTokenClaim } from '../lib/jwt';
import { FirebaseSignedOutError, getFreshFirebaseIdToken, signOutOfFirebase } from '../lib/firebaseAuth';
import {
  FIREBASE,
  fetchLoginProviderConfig,
  firebaseUidOf,
  isFirebaseToken,
  loginPageUrl,
  storedSessionProvider,
} from '../lib/loginProvider';
import { getGrants, getUserId, getUserPartners, isAuthenticated, nullifySession } from '../lib/user';
import { SIGN_IN_FAILED, signInRefusalCode } from '../lib/signInRefusals';
import { updateNextSignablePartner } from '../lib/unsignedPartners';
import { useUserProfilePicture } from './UserProfilePictureContext';

/**
 * Bounds the best-effort server logout so a stalled request cannot hold up the redirect that follows.
 */
const SERVER_LOGOUT_TIMEOUT_MS = 5000;

/**
 * Firebase session exchange statuses that end the session: a rejected token (401) or an account
 * association failure (409, 422). Any other failure may be temporary and keeps it.
 */
const FIREBASE_SIGN_IN_AGAIN_STATUSES = [401, 409, 422];

/**
 * Bounds the Firebase session exchange, so a stalled response cannot hold the shared renewal open.
 */
const SESSION_EXCHANGE_TIMEOUT_MS = 15000;

/**
 * Reduces a thrown value to plain fields for the context argument of `logger.error`.
 *
 * A structured log backend can merge that argument into the log event, so passing an axios error
 * directly would let the error's own `message` replace the log message and copy its request config.
 *
 * @param {*} err - Value thrown by a failed operation.
 * @returns {Object} The error's kind, message and stack under `error`, plus any HTTP status.
 */
function errorLogContext(err) {
  return {
    error: {
      kind: err?.name,
      message: err?.message,
      stack: err?.stack,
    },
    status: err?.response?.status,
  };
}

/**
 * @typedef {Object} SignInFailure
 * @property {string} code - The `/auth-error` code: a refusal code, or `signin_failed`.
 * @property {string} category - `refused`, `http_error`, `network_error`, `logout_failed` or
 *   `unexpected`.
 * @property {number} [status] - The response's HTTP status, when there was a response.
 */

/**
 * Builds a sign-in error without the axios error's URL or headers, which carry the authorization
 * code, nonce and email token.
 *
 * @param {string} request - `logout` or `callback`, the request that failed.
 * @param {*} err - The thrown value.
 * @returns {Error & { signInFailure: SignInFailure }} An error holding only code, category and status.
 */
function signInExchangeError(request, err) {
  const status = Number.isInteger(err?.response?.status) ? err.response.status : undefined;
  const refusalCode = request === 'callback' ? signInRefusalCode(status, err?.response?.data?.code) : undefined;
  let category;
  if (refusalCode) {
    category = 'refused';
  } else if (request === 'logout') {
    category = 'logout_failed';
  } else if (status) {
    category = 'http_error';
  } else {
    category = err?.isAxiosError ? 'network_error' : 'unexpected';
  }
  return Object.assign(new Error(`the sign-in ${ request } request failed`), {
    signInFailure: { code: refusalCode || SIGN_IN_FAILED, category, status },
  });
}

/**
 * Auth context for browser-side authentication state and actions.
 *
 * This module owns the OAuth flow, token refresh, session persistence, and
 * auth-driven redirects used throughout the legacy React application.
 */
const AuthContext = createContext(null);

/**
 * Provides auth state/actions to descendants.
 *
 * @param {Object} params
 * @param {JSX.Element} params.children - Child tree that can consume auth.
 * @returns {JSX.Element} Auth provider wrapper.
 */
function AuthProvider({ children }) {
  const [, setPictureUrl] = useUserProfilePicture();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [auth] = useState(
    setupAuth({
      history,
      queryClient,
      setPictureUrl,
    }),
  );
  return useMemo(() => (
    <AuthContext.Provider value={ auth }>
      { children }
    </AuthContext.Provider>
  ), [auth, children]);
}

AuthProvider.propTypes = {
  children: PropTypes.element,
};

/**
 * Returns the auth API from context.
 *
 * @returns {Object|null} Auth API object or null outside provider.
 */
function useAuth() {
  const context = useContext(AuthContext);
  if (context === null) {
    logger.error('useAuth() must be called within a AuthProvider');
  }
  return context;
}

/**
 * Builds the auth API exposed by AuthContext.
 *
 * @param {Object} params
 * @param {Object} params.history - react-router history instance.
 * @param {Object} params.queryClient - React Query client.
 * @param {Function} params.setPictureUrl - Profile picture setter.
 * @returns {Object} Auth API consumed by the app.
 */
function setupAuth({ history, queryClient, setPictureUrl }) {
  let webAuth;
  let invalidLoginSessionEnding = null;
  let firebaseRenewal = null;

  /**
   * Gets the authenticated email persisted in localStorage.
   *
   * @returns {string|null} Authenticated email when available.
   */
  function authenticatedEmail() {
    return localStorage.getItem('email');
  }

  /**
   * Resolves the callback origin used in OAuth code exchange.
   *
   * @returns {string} Allowed callback origin.
   */
  function getCallbackOrigin() {
    const currentOrigin = window.location.origin;
    // Even if test logins are enabled, don't use current origin if this is being rendered from the server side.
    return (process.env.REACT_APP_ALLOW_TEST_LOGINS && (process.env.REACT_APP_URL !== currentOrigin))
      ? currentOrigin
      : process.env.REACT_APP_NEXT_JS_URL;
  }

  /**
   * Exchanges the authorization code for tokens in the backend.
   *
   * It clears the local session and query cache first, keeping the saved destination and request
   * attribution for a later sign-in, then signs out of Firebase and the server. Either failure stops the
   * exchange.
   *
   * @param {Object} params
   * @param {string} params.code - OAuth authorization code.
   * @param {string} params.nonce - OAuth nonce from browser storage.
   * @returns {Promise<Object>} Axios response containing auth payload.
   * @throws {Error} From {@link signInExchangeError} when either request fails.
   */
  async function exchangeAuthorizationCode({ code, nonce }) {
    const partner = sessionStorage.getItem('partner') ? sessionStorage.getItem('partner') : '';
    const goalPage = sessionStorage.getItem('goal-page') ? sessionStorage.getItem('goal-page') : '';
    const defaultDistrictId = sessionStorage.getItem('defaultDistrictId') ? sessionStorage.getItem('defaultDistrictId') : '';
    const emailToken = sessionStorage.getItem('emailToken') || '';
    const callbackOrigin = getCallbackOrigin();
    nullifySession(queryClient);
    try {
      // End the shared Firebase sign-in before replacing the session.
      await signOutOfFirebase({ bestEffort: false });
      await axios.get(
        `${ process.env.REACT_APP_API_URL }/v1/oauth2/logout`,
        { withCredentials: true },
      );
    } catch (err) {
      throw signInExchangeError('logout', err);
    }
    try {
      return await axios.get(`${ process.env.REACT_APP_API_URL }/v1/oauth2/callback?code=${ code }&nonce=${ nonce }&partner=${ partner }&goalPage=${ goalPage }&defaultDistrictId=${ defaultDistrictId }&callbackOrigin=${ callbackOrigin }`, {
        headers: { Authorization: 'Bearer '.concat(`::${ emailToken }`) },
        withCredentials: true,
      });
    } catch (err) {
      throw signInExchangeError('callback', err);
    }
  }

  /**
   * Forces user back to login flow and stores current location.
   */
  function forceLogin() {
    // Remember where we were so we can navigate back post login
    rememberLocation();
    logger.error('forceLogin: location stored, redirecting to /');
    history.replace('/');
  }

  /**
   * Returns a valid API access token, refreshing when needed.
   *
   * A successful refresh saves the new session and returns its token without navigating; pages and
   * route guards decide where the user belongs. The one exception is a duplicate-email result,
   * which logs the user out to `/auth-error` and rejects. The stored session's issuer picks the
   * refresh: an Auth0 session uses the server's refresh cookie, and a Firebase session goes
   * through `renewFirebaseSession()`.
   *
   * @param {Object} [options] - Options for token retrieval.
   * @param {boolean} [options.skipRedirectOnFailure=false] - If true, skips
   *   redirecting to / when token refresh fails. Useful when the
   *   caller wants to handle failures differently (e.g., redirect to login).
   * @returns {Promise<string>} Access token when acquisition succeeds.
   * @throws {Error} When acquisition fails, including after a redirect.
   */
  async function getAccessToken(options = {}) {
    const { skipRedirectOnFailure = false } = options;
    //  check to see if the access token is still valid, then get a new one if it isn't
    const accessToken = localStorage.getItem('access_token') || '';
    const emailToken = sessionStorage.getItem('emailToken') || '';
    const partner = sessionStorage.getItem('partner') ? sessionStorage.getItem('partner') : '';
    let token;
    if (isAuthenticated() && accessToken !== '') {
      if (isQualified() && isEmailVerified()) {
        token = accessToken;
      } else {
        // If user isn't qualified, check to see if the database has changed since
        //  last checked.  If not, then return the token we have.  If it has changed,
        //  and the user is now qualified, then don't return what we have, and instead
        //  fall down to the next code block to refresh the token to reflect the newly
        //  qualified status
        try {
          const userData = await axios({
            method: 'PATCH',
            headers: {
              Authorization: 'Bearer '.concat(`${ accessToken }:${ emailToken }`),
            },
            url: `${ process.env.REACT_APP_API_URL }/v1/users/self`,
          });
          const user = userData.data;
          tracker.logEvent('Auth:function:getAccessToken', {
            status: 200,
            message: 'got userData from PATCH self',
            emailToken: emailToken.length,
            accessToken: accessToken.length,
            serverQualState: user.qual_state,
            localQualState: localStorage.getItem('user_qualified_state'),
            serverEmailVerified: user.email_verified,
            localEmailVerified: extraTokenClaim('emailVerified'),
          });
          // If nothing has changed, then just return the existing token
          if (
            user.qual_state === localStorage.getItem('user_qualified_state')
            && user.email_verified === extraTokenClaim('emailVerified')
          ) {
            token = accessToken;
          }
        } catch (err) {
          tracker.logEvent('Auth:function:getAccessToken', {
            status: err.response?.status || 500,
            message: 'error PATCHing self',
            emailToken: emailToken.length,
            accessToken: accessToken.length,
            localQualState: localStorage.getItem('user_qualified_state'),
            localEmailVerified: extraTokenClaim('emailVerified'),
          });
          if (err.response?.status === 404) {
            token = accessToken;
          } else if (err.response?.status === 409) {
            if (isInvalidLoginError(err)) {
              await endInvalidLoginSession(err.response.data);
            }
            throw err;
          }
        }
      }
    }
    if (token) {
      return token;
    }
    if (storedSessionProvider() === FIREBASE) {
      return renewFirebaseSession({ emailToken, skipRedirectOnFailure });
    }
    let refreshedToken;
    try {
      const results = await axios({
        method: 'POST',
        withCredentials: true,
        url: `${ process.env.REACT_APP_API_URL }/v1/oauth2/token`,
        headers: {
          Authorization: `Bearer ::${ emailToken }`,
        },
        data: {
          partner,
        },
      });
      const authResult = results.data;
      if (!isNonEmptyString(authResult?.accessToken) || !isNonEmptyString(authResult?.idToken)) {
        throw new Error('Token refresh did not return session tokens');
      }
      await applyRefreshedSession(authResult);
      refreshedToken = authResult.userError?.duplicateEmail ? null : localStorage.getItem('access_token');
    } catch (err) {
      // If we attempted to update the email to a dup email, trap the error here
      if (err.response && err.response.data && err.response.data.duplicateEmail && err.response.data.status === 409) {
        tracker.logEvent('Auth:function:getAccessToken', {
          status: err.response.data.status,
          message: 'error getting tokens',
        });
        await logout({ returnTo: `/auth-error?provider=${ err.response.data.provider }&duplicate=${ err.response.data.duplicateProvider }&referenceCode=${ err.response.data.userError.referenceCode }` });
      } else if (err.response && err.response.status && err.response.status === 401) {
        // in this case, we have a specific error response, so we know the user needs to log back in
        nullifySession(queryClient);
        await axios.get(`${ process.env.REACT_APP_API_URL }/v1/oauth2/logout`, { withCredentials: true });
        tracker.logEvent('Auth:function:getAccessToken', {
          status: 401,
          message: 'unauthorized error getting tokens',
        });
        if (!skipRedirectOnFailure) {
          forceLogin();
        }
      } else {
        tracker.logEvent('Auth:function:getAccessToken', {
          status: err.response && err.response.data ? err.response.data.status : 500,
          message: 'unspecified error getting tokens',
        });
        // If this has failed twice before, then log the user out
        //  Don't want to do it the first time, in case it's just a network blip - don't
        //  unnecessarily log the user out if we don't have to.
        const tokenFailureCount = parseInt(sessionStorage.getItem('tokenFailureCount') || '0') + 1;
        sessionStorage.setItem('tokenFailureCount', `${ tokenFailureCount }`);
        if (tokenFailureCount > 2) {
          nullifySession(queryClient);
          await axios.get(`${ process.env.REACT_APP_API_URL }/v1/oauth2/logout`, { withCredentials: true });
          tracker.logEvent('Auth:function:getAccessToken', {
            status: 401,
            message: 'too many failures - logging the user out',
          });
          sessionStorage.removeItem('tokenFailureCount');
        }
        if (!skipRedirectOnFailure) {
          forceLogin();
        }
      }
      // Reject even after redirecting: navigation does not stop pending callers from sending requests.
      throw err;
    }
    if (!isNonEmptyString(refreshedToken)) {
      throw new Error('Token refresh did not establish an authenticated session');
    }
    return refreshedToken;
  }

  /**
   * Renews a Firebase session: mints a fresh ID token for the signed-in Firebase user and exchanges
   * it at `/v1/oauth2/session` for a new session, as `refreshFirebaseSessionAndGetToken` in
   * `swing-left/frontend` does. Only the email token goes with it: attribution belongs to the
   * login that established the session. Concurrent callers share one attempt.
   *
   * - Success saves the session without navigating and returns its access token.
   * - No usable Firebase user, a Firebase user other than the session's, or a 401, 409 or 422 from
   *   the exchange ends the session. A `duplicateEmail` or `invalidLogin` conflict goes to
   *   `/auth-error`; anything else clears the session and, unless `skipRedirectOnFailure` is set,
   *   forces a new login that keeps the destination.
   * - Any other failure, such as a network error, a 403 or 5xx from the exchange, or a Firebase
   *   error that may be temporary, keeps the session and does not count toward the Auth0 refresh's
   *   three-failure logout. Unless `skipRedirectOnFailure` is set, it redirects as a failed Auth0
   *   refresh does.
   * - When another tab ends or replaces the stored session during the attempt, the attempt stops:
   *   nothing more is stored, and nothing is cleared or redirected.
   *
   * @param {Object} params
   * @param {string} params.emailToken - Outstanding email verification token, or empty.
   * @param {boolean} params.skipRedirectOnFailure - Whether to skip redirecting on failure.
   * @returns {Promise<string>} The new access token.
   * @throws {Error} Whenever no new session was saved, including after a redirect.
   */
  async function renewFirebaseSession({ emailToken, skipRedirectOnFailure }) {
    if (!firebaseRenewal) {
      firebaseRenewal = attemptFirebaseRenewal(emailToken).finally(() => {
        firebaseRenewal = null;
      });
    }
    const outcome = await firebaseRenewal;
    if (outcome.token) {
      return outcome.token;
    }
    // Callers sharing the attempt redirect once; a second forceLogin would record `/` as the destination.
    if (outcome.redirect && !skipRedirectOnFailure && !outcome.redirected) {
      outcome.redirected = true;
      forceLogin();
    }
    throw outcome.error;
  }

  /**
   * Makes one Firebase renewal attempt for `renewFirebaseSession()`.
   *
   * @param {string} emailToken - Outstanding email verification token, or empty.
   * @returns {Promise<Object>} `{ token }` on success; otherwise `{ error, redirect }`, where
   *   `redirect` says whether the caller should force a new login. Never rejects.
   */
  async function attemptFirebaseRenewal(emailToken) {
    let sessionIdToken = localStorage.getItem('id_token');
    let sessionUserId = localStorage.getItem('user_id');
    const sessionUid = firebaseUidOf(sessionIdToken);
    // The Next.js app and other tabs share this storage, so the session can end or be replaced, even
    // by the same account, while the attempt waits.
    const isStillCurrent = () => localStorage.getItem('id_token') === sessionIdToken
      && localStorage.getItem('user_id') === sessionUserId;
    const sessionChanged = () => ({ error: new Error('The session changed while it was renewing'), redirect: false });
    let fresh;
    try {
      fresh = await getFreshFirebaseIdToken();
    } catch (err) {
      if (!isStillCurrent()) {
        return sessionChanged();
      }
      return err instanceof FirebaseSignedOutError
        ? endFirebaseSession(err, err.message)
        : keepFirebaseSession(err);
    }
    if (!isStillCurrent()) {
      return sessionChanged();
    }
    // The Firebase user may now be someone else; renewing would silently switch accounts.
    if (fresh.user.uid !== sessionUid) {
      const err = new FirebaseSignedOutError('the Firebase user is not the session\'s');
      return endFirebaseSession(err, err.message);
    }
    let authResult;
    try {
      const results = await axios({
        method: 'POST',
        url: `${ process.env.REACT_APP_API_URL }/v1/oauth2/session`,
        headers: {
          Authorization: `Bearer ::${ emailToken }`,
        },
        data: {
          idToken: fresh.idToken,
        },
        timeout: SESSION_EXCHANGE_TIMEOUT_MS,
      });
      authResult = results.data;
    } catch (err) {
      if (!isStillCurrent()) {
        return sessionChanged();
      }
      const status = err.response?.status;
      if (!FIREBASE_SIGN_IN_AGAIN_STATUSES.includes(status)) {
        return keepFirebaseSession(err);
      }
      const body = err.response.data || {};
      // Accept conflict details at the top level or inside `userError`.
      const conflict = status === 409 ? { ...body, ...body.userError } : {};
      if (conflict.duplicateEmail) {
        await logoutDuplicateEmail(conflict);
        return { error: err, redirect: false };
      }
      if (conflict.invalidLogin) {
        await endInvalidLoginSession(conflict);
        return { error: err, redirect: false };
      }
      return endFirebaseSession(err, `exchange refused: ${ status } ${ body.code || '' }`.trim());
    }
    if (!isStillCurrent()) {
      return sessionChanged();
    }
    if (!isNonEmptyString(authResult?.accessToken) || !isNonEmptyString(authResult?.idToken)
      || !isNonEmptyString(authResult?.idTokenPayload?.sub)) {
      return keepFirebaseSession(new Error('Firebase session renewal did not return session tokens'));
    }
    // From here the current session is the one this attempt stores.
    const previousSession = { idToken: sessionIdToken, userId: sessionUserId };
    sessionIdToken = authResult.idToken;
    sessionUserId = authResult.idTokenPayload.sub;
    try {
      await applyRefreshedSession(authResult, { pictureUrl: fresh.user.photoURL, isCurrent: isStillCurrent });
    } catch (err) {
      // A write that failed, such as on full storage, leaves this session or the previous one stored.
      const previousStillStored = localStorage.getItem('id_token') === previousSession.idToken
        && localStorage.getItem('user_id') === previousSession.userId;
      return isStillCurrent() || previousStillStored ? keepFirebaseSession(err) : sessionChanged();
    }
    if (authResult.userError?.duplicateEmail) {
      return { error: new Error('Token refresh did not establish an authenticated session'), redirect: false };
    }
    // Storing waits on partner lookups, during which another tab can sign in.
    if (!isStillCurrent()) {
      return sessionChanged();
    }
    return { token: authResult.accessToken };
  }

  /**
   * Clears an unrenewable Firebase session before asking for a login, or the login page would send
   * it straight back here. Skips the redirect if another tab signed in while clearing.
   *
   * @param {*} err - The failure.
   * @param {string} reason - Why, for the tracker event.
   * @returns {Promise<Object>} `{ error: err, redirect }`.
   */
  async function endFirebaseSession(err, reason) {
    tracker.logEvent('Auth:function:getAccessToken', {
      status: 401,
      message: `firebase session ended: ${ reason }`,
    });
    await clearSession();
    return { error: err, redirect: !localStorage.getItem('id_token') };
  }

  /**
   * Keeps the session after a renewal failure that may be temporary, and logs it.
   *
   * @param {*} err - The failure.
   * @returns {Object} `{ error: err, redirect: true }`.
   */
  function keepFirebaseSession(err) {
    logger.error('renewFirebaseSession: the session did not renew, keeping it', errorLogContext(err));
    tracker.logEvent('Auth:function:getAccessToken', {
      status: err?.response?.status || 500,
      message: 'firebase session renewal failed',
    });
    return { error: err, redirect: true };
  }

  /**
   * Attempts to refresh the user's session tokens without redirecting on failure.
   *
   * Useful for protected routes that want to try silent token refresh before
   * redirecting to login. A successful refresh leaves the user on the current
   * page. Returns true if tokens were refreshed successfully, false otherwise.
   *
   * @returns {Promise<boolean>} True if refresh succeeded, false otherwise.
   */
  async function tryRefreshToken() {
    try {
      const token = await getAccessToken({ skipRedirectOnFailure: true });
      return !!token && isAuthenticated();
    } catch {
      return false;
    }
  }

  /**
   * Returns any local letter adoption override value.
   *
   * @returns {string|null} Letter adoption override flag.
   */
  function getLetterAdoptionOverride() {
    return localStorage.getItem('letter_adoption_override');
  }

  /**
   * Lazily creates and returns the Auth0 WebAuth client.
   *
   * @returns {Promise<Object>} Auth0 WebAuth client instance.
   */
  async function getWebAuth() {
    if (!webAuth) {
      // eslint-disable-next-line import/no-named-as-default-member
      webAuth = new auth0.WebAuth({
        domain: await deprecatedConfig.get('auth0BrowserDomain') || await deprecatedConfig.get('auth0Domain'),
        clientID: await deprecatedConfig.get('auth0ClientId'),
        redirectUri: `${ process.env.REACT_APP_NEXT_JS_URL }/callback`,
        audience: await deprecatedConfig.get('auth0Id'),
        responseType: 'code',
        scope: 'openid profile email read:user offline_access',
      });
    }
    return webAuth;
  }

  /**
   * Handles OAuth callback query params and completes authentication.
   *
   * A failed exchange leaves the user signed out on `/auth-error`, with the API's refusal code when
   * it is one the page explains, or `signin_failed` otherwise.
   *
   * @param {Object} params
   * @param {Object} params.location - react-router location object.
   * @returns {Promise<void|*>} Result of auth result handler when successful.
   */
  async function handleAuthentication({ location }) {
    const params = queryString.parse(location.search);
    if (params.state !== localStorage.getItem('oauth_state')) {
      logger.error('there was an error logging in');
      tracker.logEvent('Auth:function:handleAuthentication', {
        status: 400,
        message: 'state does not match oauth_state',
        localState: localStorage.getItem('oauth_state'),
        urlState: params.state,
        urlKeys: params ? Object.keys(params).join(',') : '',
      });
      history.replace('/');
    } else {
      let results;
      try {
        results = await exchangeAuthorizationCode({
          code: params.code,
          nonce: localStorage.getItem('oauth_nonce'),
        });
      } catch (err) {
        const failure = err?.signInFailure || { code: SIGN_IN_FAILED, category: 'unexpected' };
        const refused = failure.category === 'refused';
        tracker.logEvent('Auth:function:handleAuthentication', {
          ...(failure.status && { status: failure.status }),
          code: failure.code,
          category: failure.category,
          message: refused ? 'the API refused the sign-in' : 'error exchanging auth code',
        });
        if (refused) {
          logger.info('handleAuthentication: the API refused the sign-in', { status: failure.status, code: failure.code });
        } else {
          logger.error('handleAuthentication: the sign-in exchange failed', { status: failure.status, category: failure.category });
        }
        history.replace(`/auth-error?${ queryString.stringify({ code: failure.code }) }`);
        return undefined;
      } finally {
        localStorage.removeItem('oauth_state');
        localStorage.removeItem('oauth_nonce');
      }
      return handleAuthResult(results.data);
    }
  }

  /**
   * Persists a sign-in's auth state and routes the user to the next post-login destination.
   *
   * Only the sign-in callback uses this. The user is on `/callback`, which has nothing to show, so
   * this picks a destination: `/auth-error` (by logging out) for a duplicate email, then email
   * verification, qualification, the saved `postLoginPath` (which it consumes), or `/dashboard`.
   * Token refreshes use `applyRefreshedSession()` instead, so they never choose a post-login
   * destination.
   *
   * @param {Object} authResult - Auth payload returned by backend token exchange.
   * @returns {Promise<void>}
   */
  async function handleAuthResult(authResult) {
    if (authResult && authResult.accessToken && authResult.idToken) {
      await setSession(authResult);
      if (authResult.userError && authResult.userError.duplicateEmail) {
        await logoutDuplicateEmail(authResult.userError);
      } else if (!isEmailVerified()) {
        if (authResult.email) {
          history.replace(`/verify-email/${ authResult.email }`);
        } else {
          history.replace(`/verify-email`);
        }
      } else if (!isQualified(authResult.qualifiedState)) {
        history.replace('/verify');
      } else {
        const postLoginPath = localStorage.getItem('postLoginPath');
        if (postLoginPath && postLoginPath !== '') {
          localStorage.removeItem('postLoginPath');
          history.replace(postLoginPath);
        } else {
          history.replace('/dashboard');
        }
      }
    }
  }

  /**
   * Persists a refreshed session without navigating.
   *
   * The user stays where they are, and `postLoginPath` is left for the next sign-in. A
   * duplicate-email result still logs out to `/auth-error`, because the server will not accept a
   * session for an email another account already uses.
   *
   * @param {Object} authResult - Auth payload returned by the backend token refresh, whose
   *   `accessToken` and `idToken` the caller has already checked.
   * @param {Object} [options] - Passed through to `setSession()`.
   * @returns {Promise<void>}
   */
  async function applyRefreshedSession(authResult, options) {
    await setSession(authResult, options);
    if (authResult.userError?.duplicateEmail) {
      await logoutDuplicateEmail(authResult.userError);
    }
  }

  /**
   * Logs out a session whose sign-in or refresh payload reports, in `userError`, an email another
   * account already uses.
   *
   * @param {Object} userError - `userError` from the auth payload.
   * @param {string} [userError.provider] - Auth provider of the rejected login.
   * @param {string} [userError.duplicateProvider] - Auth provider of the existing account.
   * @param {string} [userError.referenceCode] - Request ID to quote to support.
   * @returns {Promise<void>}
   */
  async function logoutDuplicateEmail({ provider, duplicateProvider, referenceCode }) {
    await logout({ returnTo: `/auth-error?provider=${ provider }&duplicate=${ duplicateProvider }&referenceCode=${ referenceCode }` });
  }

  /**
   * Checks whether the current user has admin privileges.
   *
   * @returns {boolean} True when current user is an admin.
   */
  function isAdmin() {
    return localStorage.getItem('is_admin') === 'true';
  }

  /**
   * Checks whether the authenticated email is verified.
   *
   * @returns {boolean} True when email is verified.
   */
  function isEmailVerified() {
    return extraTokenClaim('emailVerified');
  }

  /**
   * Checks whether the user can proceed to qualified-only flows.
   *
   * @param {string} [qualifiedStateValue] - Optional qualified state override.
   * @returns {boolean} True when state is qualified or super_qualified.
   */
  function isQualified(qualifiedStateValue) {
    const qualifiedState = qualifiedStateValue || localStorage.getItem('user_qualified_state');
    return qualifiedState && ['qualified', 'super_qualified'].indexOf(qualifiedState) >= 0;
  }

  /**
   * Starts sign-in with the provider the runtime setting selects, read fresh on every call.
   *
   * With Firebase as the default, or a setting that cannot be read, this goes to the shared
   * Next.js `/login` page, which shows the Firebase form or offers a retry, with the stored
   * `postLoginPath` as `returnTo`. That page sends a signed-in arrival straight on, so a signed-in
   * session is cleared first, keeping the email token it reads. With Auth0 as the default, or a
   * server that predates the setting, this starts Auth0's authorization flow as before.
   *
   * @param {boolean} showLoginPage - Legacy unused flag kept for compatibility.
   * @param {Object} [extraArgs={}] - Additional authorize params for Auth0. The login page takes
   *   `email_registration` (as `mode=register`), `login_hint` and `connection`.
   * @returns {Promise<void>}
   */
  async function login(showLoginPage, extraArgs = {}) {
    const providerConfig = await fetchLoginProviderConfig();
    if (providerConfig.status === 'unavailable' || providerConfig.defaultLoginProvider === FIREBASE) {
      if (isAuthenticated()) {
        const emailToken = sessionStorage.getItem('emailToken');
        await clearSession();
        if (emailToken) {
          sessionStorage.setItem('emailToken', emailToken);
        }
      }
      window.location.assign(loginPageUrl({
        returnTo: localStorage.getItem('postLoginPath'),
        register: extraArgs.email_registration === true,
        loginHint: extraArgs.login_hint,
        connection: extraArgs.connection,
      }));
      return;
    }
    const stateNoncePromises = [];
    for (let i = 0; i < 2; i++) {
      stateNoncePromises.push(axios({
        method: 'GET',
        url: `${ process.env.REACT_APP_API_URL }/v1/random`,
      }));
    }
    await getWebAuth();
    const values = await Promise.all(stateNoncePromises);
    localStorage.setItem('oauth_state', values[0].data);
    localStorage.setItem('oauth_nonce', values[1].data);
    webAuth.authorize({
      state: values[0].data,
      nonce: values[1].data,
      ...extraArgs,
    });
  }

  /**
   * Logs out the user locally and at the session's provider, then redirects.
   *
   * The stored session's issuer picks the provider. An Auth0 session ends with Auth0's logout
   * redirect. A Firebase session has no Auth0 session to end, so it goes straight to `returnTo`,
   * and its server logout is always best effort. Either way, the browser is also signed out of
   * Firebase, which ends the sign-in the Next.js app shares.
   *
   * @param {Object} [params]
   * @param {string} [params.returnTo=''] - App path to redirect to after logout.
   * @param {boolean} [params.bestEffortServerLogout=false] - If true, a failed or slow request to
   *   clear the server's refresh cookie is logged and bounded by a timeout instead of stopping the
   *   Auth0 logout. Local session state is already cleared by then, so stopping there would leave
   *   the user on the current page, half logged out and without the `returnTo` page.
   * @returns {Promise<void>}
   * @throws {Error} For an Auth0 session, when the Auth0 client or its configuration cannot be
   *   loaded, before any session state is cleared; also when the server logout fails, unless
   *   `bestEffortServerLogout` is set.
   */
  async function logout({ returnTo = '', bestEffortServerLogout = false } = {}) {
    tracker.logEvent('Auth:function:logout');
    //Use current location rather than configured value, to support pass-through pages from Next.js.
    const appUrl = window.location.origin;
    if (storedSessionProvider() === FIREBASE) {
      await clearSession();
      window.location.assign(`${ appUrl }${ returnTo }`);
      return;
    }
    const webAuth = await getWebAuth();
    const clientId = await deprecatedConfig.get('auth0ClientId');
    nullifySession(queryClient);
    try {
      await serverLogout({ bestEffort: bestEffortServerLogout, nextStep: 'Auth0 logout' });
    } finally {
      // Local state is already cleared, so end the shared Firebase sign-in even when the server logout fails.
      await signOutOfFirebase();
    }
    webAuth.logout({
      returnTo: `${ appUrl }${ returnTo }`,
      client_id: clientId,
    });
  }

  /**
   * Clears this browser's session without visiting Auth0: local state, then the server's refresh
   * cookie and the Firebase sign-in the Next.js app shares, both best effort. A Firebase sign-out
   * that fails or times out leaves that sign-in for the Next.js app to renew.
   *
   * @returns {Promise<void>}
   */
  async function clearSession() {
    nullifySession(queryClient);
    await Promise.all([
      serverLogout({ bestEffort: true, nextStep: 'local logout' }),
      signOutOfFirebase(),
    ]);
  }

  /**
   * Asks the server to clear its refresh-token cookie.
   *
   * @param {Object} params
   * @param {boolean} params.bestEffort - Log a failure, and bound the request with a timeout,
   *   instead of throwing.
   * @param {string} params.nextStep - What logout does next, for the failure log message.
   * @returns {Promise<void>}
   */
  async function serverLogout({ bestEffort, nextStep }) {
    const serverLogoutUrl = `${ process.env.REACT_APP_API_URL }/v1/oauth2/logout`;
    if (!bestEffort) {
      await axios.get(serverLogoutUrl, { withCredentials: true });
      return;
    }
    try {
      await axios.get(serverLogoutUrl, {
        withCredentials: true,
        timeout: SERVER_LOGOUT_TIMEOUT_MS,
      });
    } catch (err) {
      logger.error(`logout: server logout failed, continuing with ${ nextStep }`, errorLogContext(err));
    }
  }

  /**
   * Persists current route so login can redirect the user back.
   */
  function rememberLocation() {
    const postLoginUrl = window.location.href;
    const postLoginPath = postLoginUrl.replace(/^https?:\/\/[^/]+/, '');
    localStorage.setItem('postLoginPath', postLoginPath);
  }

  /**
   * Ends a session whose token has no matching login record, and explains why on `/auth-error`.
   *
   * Retrying cannot recover this session, and every page that loads with it fails the same way, so
   * without this the browser cycles between `/` and `/dashboard`. Requests made through the
   * authenticated API client and the `users/self` check in `getAccessToken` call this; direct axios
   * calls do not. Only one logout runs at a time: calls made while it is in progress wait for it
   * rather than starting another, so their callers reject with the 409 once it settles.
   *
   * The server logout is best effort, so the user still reaches `/auth-error` when it fails. A failure
   * before any session state is cleared (loading the Auth0 client or its configuration) is logged
   * rather than thrown, so callers still reject with the original 409. Calls that were waiting on that
   * attempt then start one fresh attempt between them, so the session is not left active merely
   * because the only attempt failed; a later invalid login can also try again.
   *
   * @param {Object} [details={}] - Body of the `invalidLogin` 409 response.
   * @param {string} [details.provider] - Auth provider of the rejected login.
   * @param {string} [details.referenceCode] - Request ID to quote to support.
   * @returns {Promise<void>}
   */
  async function endInvalidLoginSession(details = {}) {
    const inFlight = invalidLoginSessionEnding;
    if (inFlight && await inFlight) {
      return;
    }
    // Either nothing was in flight, or the attempt this call waited on failed and cleared the guard.
    // The first waiter to get here starts the retry, and any others wait on that one.
    if (!invalidLoginSessionEnding) {
      invalidLoginSessionEnding = attemptInvalidLoginSessionEnd(details);
    }
    await invalidLoginSessionEnding;
  }

  /**
   * Makes one attempt to log out a session whose token has no matching login record.
   *
   * @param {Object} details - Body of the `invalidLogin` 409 response.
   * @param {string} [details.provider] - Auth provider of the rejected login.
   * @param {string} [details.referenceCode] - Request ID to quote to support.
   * @returns {Promise<boolean>} True when logout reached its final redirect; false when it failed
   *   and the guard was cleared so another attempt can run.
   */
  async function attemptInvalidLoginSessionEnd({ provider, referenceCode }) {
    tracker.logEvent('Auth:function:endInvalidLoginSession', {
      status: 409,
      message: 'no login record for this session',
    });
    const params = queryString.stringify({ invalidLogin: true, provider, referenceCode });
    try {
      await logout({ returnTo: `/auth-error?${ params }`, bestEffortServerLogout: true });
      return true;
    } catch (err) {
      invalidLoginSessionEnding = null;
      logger.error('endInvalidLoginSession: logout failed', errorLogContext(err));
      return false;
    }
  }

  /**
   * Persists auth payload to browser storage and initializes profile metadata.
   *
   * The profile picture comes from Auth0 for an Auth0 session. A Firebase session has no Auth0
   * profile, so it uses the Firebase user's photo when there is one and otherwise keeps the
   * current picture.
   *
   * @param {Object} authResult - Auth payload returned by backend token exchange.
   * @param {Object} [options]
   * @param {string} [options.pictureUrl] - The Firebase user's photo, for a Firebase session.
   * @param {Function} [options.isCurrent] - For a renewal, whether the session it stored is still the
   *   stored one; the partner and picture updates after the lookup are skipped once it is not.
   * @returns {Promise<void>}
   */
  async function setSession(authResult, { pictureUrl, isCurrent } = {}) {
    // Decided before the partner lookup below, during which another tab can change the stored session.
    const firebaseSession = isFirebaseToken(authResult.idToken);
    const expiresAt = JSON.stringify(
      (authResult.expiresIn * 1000) + new Date().getTime(),
    );
    localStorage.setItem('access_token', authResult.accessToken);
    localStorage.setItem('id_token', authResult.idToken);
    localStorage.setItem('expires_at', expiresAt);
    localStorage.setItem('user_id', authResult.idTokenPayload.sub);
    localStorage.setItem('email', authResult.email);
    localStorage.setItem('grants', JSON.stringify(authResult.grants));
    localStorage.setItem('groups', JSON.stringify(authResult.groups));
    localStorage.setItem('user_name', authResult.idTokenPayload.name);
    localStorage.setItem('partners', JSON.stringify(authResult.partners));
    localStorage.setItem('is_admin', authResult.isAdmin);
    tracker.setUserId(authResult.email);
    // Unset the following values to trigger recalling the token-exchange endpoint to get a new access_token_supplement
    localStorage.setItem('user_qualified_state', authResult.qualifiedState);
    if (authResult.letterAdoptionOverride) {
      localStorage.setItem('letter_adoption_override', authResult.letterAdoptionOverride);
    }

    // if user is logging in with invalid credentials it will cause an infinite redirect
    if (!(authResult.userError.invalidLogin || authResult.userError.duplicateEmail)) {
      // Pass the stored token: getAccessToken would re-enter the renewal waiting on this lookup.
      await updateNextSignablePartner({ getAccessToken: () => authResult.accessToken }, { isCurrent });
    }
    if (isCurrent && !isCurrent()) {
      return;
    }

    if (firebaseSession) {
      if (pictureUrl) {
        setPictureUrl(pictureUrl);
      }
    } else {
      await getWebAuth();
      webAuth.client.userInfo(authResult.accessToken.split(':')[0], (err, profile) => {
        setPictureUrl(profile.picture);
      });
    }
    sessionStorage.removeItem('tokenFailureCount');
  }

  /**
   * Gets the display name of the authenticated user.
   *
   * @returns {string|null} Saved user name.
   */
  function getUserName() {
    return localStorage.getItem('user_name');
  }

  const auth = {
    authenticatedEmail,
    endInvalidLoginSession,
    forceLogin,
    getAccessToken,
    getGrants,
    getLetterAdoptionOverride,
    getUserId,
    getUserName,
    getUserPartners,
    handleAuthentication,
    isAdmin,
    isAuthenticated,
    isEmailVerified,
    isQualified,
    login,
    logout,
    rememberLocation,
    tryRefreshToken,
  };
  const tracker = new Tracker({ auth });
  return auth;
}

export { AuthContext, AuthProvider, setupAuth as __setupAuth, useAuth };
